import { useCallback, useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";
import { createRestriction, deleteRestriction, listRestrictions, patchRestriction } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const CATEGORIES = ["material", "equipment", "design", "contract", "labor", "environmental", "other"];
const STATUSES = ["open", "in_progress", "resolved", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "critical"];

const priorityColor = {
  low: "text-zinc-500 bg-zinc-100",
  medium: "text-cs-blueprint bg-blue-50",
  high: "text-amber-700 bg-amber-50",
  critical: "text-red-700 bg-red-50",
};
const statusColor = {
  open: "text-cs-blueprint",
  in_progress: "text-amber-700",
  resolved: "text-cs-success",
  cancelled: "text-zinc-400",
};

export default function RestrictionsTable({ projectId }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", category: "other", priority: "medium", responsible: "", due_date: "", status: "open" });

  const refresh = useCallback(async () => {
    setLoading(true);
    try { const d = await listRestrictions(projectId); setItems(d.restrictions || []); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { refresh(); }, [refresh]);

  const onCreate = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (!payload.due_date) delete payload.due_date;
      await createRestriction(projectId, payload);
      toast.success(t("success"));
      setShowCreate(false);
      setForm({ title: "", description: "", category: "other", priority: "medium", responsible: "", due_date: "", status: "open" });
      await refresh();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const updateStatus = async (id, status) => {
    try { await patchRestriction(id, { status }); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try { await deleteRestriction(id); toast.success(t("success")); await refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  return (
    <div data-testid="restrictions-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold text-zinc-900">{t("restrictions")}</h3>
        <button onClick={() => setShowCreate(true)} className="inline-flex items-center gap-2 bg-cs-accent text-white px-3 py-2 rounded-md text-sm font-semibold hover:bg-cs-accentHover" data-testid="restriction-add-btn">
          <Plus size={14} /> {t("add_restriction")}
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">{t("loading")}</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-md p-10 text-center bg-white" data-testid="restrictions-empty">
          <p className="text-zinc-500 text-sm">{t("no_restrictions")}</p>
        </div>
      ) : (
        <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50">
              <tr className="text-left">
                <th className="cs-label px-4 py-3">{t("title")}</th>
                <th className="cs-label px-4 py-3">{t("category")}</th>
                <th className="cs-label px-4 py-3">{t("responsible")}</th>
                <th className="cs-label px-4 py-3">{t("due_date")}</th>
                <th className="cs-label px-4 py-3">{t("priority")}</th>
                <th className="cs-label px-4 py-3">{t("status")}</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r, idx) => (
                <tr key={r.id} className={`border-t border-zinc-100 ${idx % 2 === 0 ? "" : "bg-zinc-50/40"}`} data-testid={`restriction-row-${r.id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{r.title}</div>
                    {r.description && <div className="text-xs text-zinc-500 mt-0.5 line-clamp-2 max-w-md">{r.description}</div>}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 capitalize">{r.category}</td>
                  <td className="px-4 py-3 text-zinc-700">{r.responsible || "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{(r.due_date || "—").toString().slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase ${priorityColor[r.priority] || "text-zinc-500 bg-zinc-100"}`}>{r.priority}</span>
                  </td>
                  <td className="px-4 py-3">
                    <select value={r.status} onChange={(e) => updateStatus(r.id, e.target.value)}
                      className={`text-xs font-mono uppercase bg-transparent border-0 focus:outline-none ${statusColor[r.status] || ""}`}
                      data-testid={`restriction-status-${r.id}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => onDelete(r.id)} className="p-1.5 text-zinc-500 hover:text-red-600 hover:bg-zinc-100 rounded" data-testid={`restriction-delete-${r.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 bg-zinc-950/40 z-50 flex items-center justify-center p-4" data-testid="create-restriction-modal">
          <div className="bg-white border border-zinc-200 rounded-md shadow-2xl w-full max-w-lg">
            <div className="px-6 py-4 border-b border-zinc-200">
              <h2 className="font-heading text-lg font-semibold">{t("add_restriction")}</h2>
            </div>
            <form onSubmit={onCreate} className="p-6 space-y-4">
              <div>
                <label className="cs-label block mb-1.5">{t("title")} *</label>
                <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="restriction-title-input" />
              </div>
              <div>
                <label className="cs-label block mb-1.5">{t("description")}</label>
                <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="restriction-description-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="cs-label block mb-1.5">{t("category")}</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="restriction-category-input">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="cs-label block mb-1.5">{t("priority")}</label>
                  <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="restriction-priority-input">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="cs-label block mb-1.5">{t("responsible")}</label>
                  <input type="text" value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm" data-testid="restriction-responsible-input" />
                </div>
                <div>
                  <label className="cs-label block mb-1.5">{t("due_date")}</label>
                  <input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="w-full px-3 py-2 border border-zinc-300 rounded-md text-sm font-mono" data-testid="restriction-due-input" />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-zinc-700 hover:bg-zinc-100 rounded-md" data-testid="restriction-cancel-btn">{t("cancel")}</button>
                <button type="submit" className="px-4 py-2 bg-cs-accent text-white text-sm font-semibold rounded-md hover:bg-cs-accentHover" data-testid="restriction-submit-btn">{t("create")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
